import React, { Fragment, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router";
import { IQuestion } from "../../../model/interface/question";
import Constant from "../../../utils/constant";
import Pagination from "../../shared/common/pagination";
import SelectPageSize from "../../shared/common/pageSizeDropdown";
import Loader from "../../shared/loader/loader";
import {
  HumanIcon,
  MachineIcon,
  SpanishTransIcon,
  TextIcon,
  VideoLegendIcon,
  AddStandardIcon,
  DiagnosticIcon,
  ApproveIcon,
  Disapprove,
  PreviewIcon,
  UnlinkIcon,
  CopyIcon,
  DeleteIcon,
} from "../../../assets/icons/index";
import { toast } from "react-toastify";
import Tooltip from "../../shared/common/ellipsisText";
import PreviewModal from "../questions/previewModal";
import ConfirmationPopup from "../../shared/common/confirmationPopup";
import constant from "../../../utils/constant";
import { IlinkedStandard } from "../../../model/interface/linkedStandard";
import DelinkStandardPopup from "../../shared/common/delinkStandardPopup";
import Copy from "../../shared/common/copy";
import BulkRemoveAssociatedQuestionPopup from "../../shared/common/bulkRemoveAssociatedQuestionPopup";
import { MinusCircleIcon } from "@heroicons/react/outline";
import { ClipboardCopyIcon } from "@heroicons/react/solid";

interface AssociatedQuestionProps {
  standardId: number;
  standardData: any;
}

function AssociatedQuestion(props: AssociatedQuestionProps) {
  const defaultPageNumber = 1;
  const defaultPageSize = 10;
  const { standardId, standardData } = props;
  const navigate = useNavigate();
  const defaultStandardQuestion: IQuestion[] = [
    {
      questionId: 0,
      questionText: "",
      domainId: 0,
      domainName: "",
      standardCode: "",
      standardId: 0,
      standardName: "",
      subjectId: 0,
      subjectName: "",
    },
  ];
  const [questions, setQuestions] = useState<IQuestion[]>(
    defaultStandardQuestion
  );
  const [pageNumber, setPageNumber] = useState(defaultPageNumber);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);
  const [open, setOpen] = useState<boolean>(false);
  const [previewId, setPreviewId] = useState<string>("");
  const [questionIdPreview, setQuestionIdPreview] = useState<number>();
  const [questionIndexPreview, setQuestionIndexPreview] = useState<number>();
  const [questionUnlink, setQuestionUnlink] = useState<number>(0);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [questionToPreview, setQuestionToPreview] = useState<
    IQuestion | undefined
  >();
  const [standardUnlink, setStandardUnlink] = useState<number>(0);
  const [showLinkedStandards, setShowLinkedStandards] =
    useState<boolean>(false);
  const [linkedStandards, setLinkedStandards] = useState<IlinkedStandard[]>([]);
  const [isDelink, setIsDelink] = useState<boolean>(false);
  const [showBulkUnlinkPopup, setShowBulkUnlinkPopup] =
    useState<boolean>(false);
  const [bulkQuestionUnlink, setBulkQuestionUnlink] = useState<string>();

  useEffect(() => {
    if (isLoaded) {
      bindAssociatedQuestion();
    }
  }, [pageNumber]);

  useEffect(() => {
    if (pageNumber === 1) {
      bindAssociatedQuestion();
      setIsLoaded(true);
    } else {
      paginationRef?.current?.onReset();
    }
  }, [pageSize]);

  const bindAssociatedQuestion = async () => {
    setLoading(true);
    const question: IQuestion = {
      standardId: standardId,
      orderbyColumn: "questionId",
      sortDir: "ASC",
      pageNumber: pageNumber,
      pageSize: pageSize,
    };
    const service = await import("../../../api/question.service");
    const response = service.getAssociatedQuestions(question);
    const data = (await response).data;
    setLoading(false);
    setQuestions(data.data);
    setTotalPages(Math.ceil(data.totalRecords / pageSize));
    setTotalRecords(data.totalRecords);
  };

  const handleRedirectAssociateQuestion = () => {
    navigate(Constant.ASSOCIATED_QUESTIONS, {
      state: {
        associateStandardId: standardId,
        associateStandardName: standardData.name,
        associateStandardData: standardData,
      },
    });
  };

  const handleRedirectAddQuestion = () => {
    localStorage.removeItem(Constant.FROM_ASSOCIATE_STANDARD_KEY);
    localStorage.setItem(
      Constant.FROM_ASSOCIATE_STANDARD_KEY,
      JSON.stringify({
        associateStandardId: standardId,
        associateStandardName: standardData.name,
        associateStandardData: standardData,
      })
    );
    window.open(Constant.AUTHORQUESTION);
  };

  function confirmQuestionUnlink(idToUnlink?: number) {
    if (idToUnlink) {
      setShowConfirmation(true);
      setQuestionUnlink(idToUnlink);
    }
  }
  async function handleShowLinkedStandardsConfimation(
    standardId: number,
    isDelink?: boolean
  ) {
    setLoading(true);

    const service = await import("../../../api/standardLinking.service");
    service.getAssociatedStandards(standardId).then((response) => {
      setLoading(false);
      if (response && response.data.length > 0) {
        setShowLinkedStandards(true);
        if (isDelink) setIsDelink(true);
        setLinkedStandards(response.data);
      } else {
        if (isDelink && standardId) {
          deleteQuestionStandardAssociation(standardId);
        }
      }
    });
  }

  const handleDelinkStandard = (confirmAction: boolean) => {
    setShowConfirmation(false);
    if (confirmAction) {
      handleShowLinkedStandardsConfimation(standardId, true);
    }
  };
  const handleDelinkConfirmation = (action: boolean) => {
    if (action) {
      setIsDelink(false);

      deleteQuestionStandardAssociation(standardId);
    } else {
      setIsDelink(false);
    }
  };
  const deleteQuestionStandardAssociation = async (standardId: number) => {
    setShowConfirmation(false);

    setLoading(true);
    const service = await import("../../../api/question.service");
    await service.deleteAssociatedQuestions({
      standardId: standardId,
      questionIds: questionUnlink,
    });
    bindAssociatedQuestion();
    setLoading(false);
    toast.success("Associated question is unlinked successfully");
  };

  const bulkDeleteQuestionStandardAssociation = async (standardId: number) => {
    setShowBulkUnlinkPopup(false);

    setLoading(true);
    const service = await import("../../../api/question.service");
    await service.deleteAssociatedQuestions({
      standardId: standardId,
      questionIds: bulkQuestionUnlink,
    });
    bindAssociatedQuestion();
    setLoading(false);
    toast.success("Associated question(s) are unlinked successfully");
  };

  const openModal = (id, questionId, question) => {
    setOpen(true);
    setPreviewId(id);
    setQuestionIdPreview(questionId);
    setQuestionToPreview(question);
  };

  const popUpClose = (value) => {
    setOpen(value);
    setPreviewId("");
    setQuestionIdPreview(undefined);
    setQuestionToPreview(undefined);
  };

  const copyAllVisibleIds = () => {
    const allIds = questions.map((question) => question.questionId);
    Copy(allIds);
  };

  useEffect(() => {
    if (bulkQuestionUnlink && bulkQuestionUnlink.length > 0) {
      bulkDeleteQuestionStandardAssociation(standardId);
    }
  }, [bulkQuestionUnlink]);

  return (
    <Fragment>
      {loading && <Loader />}
      {showConfirmation && (
        <ConfirmationPopup
          title={"Unlink Associated Question"}
          confirmAction={handleDelinkStandard}
          message={
            "Are you sure you want to unlink? It will unlink from all the linked standards."
          }
          buttonText={"Yes"}
        />
      )}
      {showBulkUnlinkPopup && (
        <BulkRemoveAssociatedQuestionPopup
          standardId={standardId}
          setShowModal={setShowBulkUnlinkPopup}
          setQuestionIdsToRemove={setBulkQuestionUnlink}
        ></BulkRemoveAssociatedQuestionPopup>
      )}
      <PreviewModal
        modal={openModal}
        show={open}
        changeValue={popUpClose}
        itemRef={previewId}
        questionIdPreview={questionIdPreview}
        questionIndexPreview={questionIndexPreview}
        setQuestionIndexPreview={setQuestionIndexPreview}
        questions={questions}
        setQuestionIdPreview={setQuestionIdPreview}
        setPreviewId={setPreviewId}
        questionToPreview={questionToPreview}
        setQuestionToPreview={setQuestionToPreview}
      />

      <div className="text-gray-900 block px-4 py-4 text-sm shadow border-b border-gray-200 rounded-b-lg">
        <div className="flex w-full mt-1 mb-4 rounded-lg">
          <div className="flex justify-end">
            <button
              onClick={() => handleRedirectAssociateQuestion()}
              type="button"
              className="flex items-center text-sm  cursor-pointer"
            >
              <AddStandardIcon />
              Associate Question
            </button>
            <button
              onClick={() => handleRedirectAddQuestion()}
              type="button"
              className="ml-6 flex items-center text-sm  cursor-pointer"
            >
              <AddStandardIcon />
              Add Question
            </button>
            <button
              onClick={() => setShowBulkUnlinkPopup(true)}
              type="button"
              className="ml-6 flex items-center text-sm text-red-500 font-semibold  cursor-pointer"
            >
              <MinusCircleIcon className="text-red-500 h-5 w-5 mr-1" />
              Bulk Remove
            </button>
          </div>
        </div>
        <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-primary-color">
              <tr>
                <th
                  scope="col"
                  className="px-3 py-2 whitespace-nowrap font-light text-sm text-left text-white dark:text-white tracking-wider"
                >
                  <span className="flex gap-2">
                    Id
                    <span title="Copy all visible IDs">
                      <ClipboardCopyIcon
                        className="h-5 cursor-pointer"
                        onClick={copyAllVisibleIds}
                      />
                    </span>
                  </span>
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 whitespace-nowrap font-light text-sm text-left text-white dark:text-white tracking-wider"
                >
                  <span className="flex">Question Text</span>
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 whitespace-nowrap font-light text-sm text-left text-white dark:text-white tracking-wider"
                >
                  <span className="flex">Type</span>
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 whitespace-nowrap font-light text-sm text-left text-white dark:text-white tracking-wider"
                >
                  <span className="flex">Classification</span>
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 whitespace-nowrap font-light text-sm text-left text-white dark:text-white tracking-wider"
                >
                  <span className="flex">DOK</span>
                </th>

                <th
                  scope="col"
                  className="px-3 py-2 font-light whitespace-wrap text-sm text-left text-white dark:text-white"
                >
                  <span className="flex gap-2">Exp</span>
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 font-light whitespace-wrap text-sm text-center text-white"
                >
                  <SpanishTransIcon className="text-white dark:text-white fill-current h-4 w-4" />
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 whitespace-nowrap font-light text-sm text-center text-white dark:text-white tracking-wider"
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {questions &&
                questions?.map((question, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-100"}
                  >
                    <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
                      <div className="flex gap-2">
                        <CopyIcon
                          className="h-5 cursor-pointer"
                          title="Copy id"
                          onClick={() => Copy([question.questionId])}
                        />
                        <a
                          className=" cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                          href={Constant.MANAGEQUESTION.replace(
                            ":id",
                            question?.questionId!.toString()
                          )}
                        >
                          {question.questionId}
                        </a>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
                      <a
                        className=" cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                        href={Constant.MANAGEQUESTION.replace(
                          ":id",
                          question?.questionId!.toString()
                        )}
                      >
                        <Tooltip text={question.questionText} />
                      </a>
                    </td>
                    <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
                      {question.questionTypeName}
                    </td>
                    <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
                      {question.questionClassification}
                    </td>
                    <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
                      {question.dok}
                    </td>
                    <td className="px-3 py-2 whitespace-wrap text-sm text-center font-normal">
                      <div className="flex items-center">
                        {question.settings?.filter((e) => {
                          return e.videoExplanation != undefined;
                        })[0]?.videoExplanation && (
                          <span className="relative w-11 inline-block">
                            <VideoLegendIcon title="Video explanation available for this subject" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-wrap text-sm text-left font-normal text-gray-500">
                      {question.isHumanTranslated ? (
                        <HumanIcon
                          title="Human Translated"
                          className="w-4 h-4"
                        />
                      ) : question.isMachineTranslated ? (
                        <MachineIcon
                          title="Machine Translated"
                          className="w-4 h-4"
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="flex justify-center items-center px-3 py-2 text-sm text-center font-normal text-gray-500">
                      <div className="flex items-center">
                        <PreviewIcon
                          onClick={() =>
                            openModal(
                              question.englishQuestionReferenceId,
                              question.questionId,
                              question
                            )
                          }
                          className="mr-3 h-5 w-5 text-xs font-light cursor-pointer text-gray-400 hover:text-secondary-color transaction ease-in-out duration-300"
                        />
                        {question.isDiagnostics && (
                          <DiagnosticIcon
                            title="Diagnostic"
                            className="mr-3 h-5 w-5"
                          />
                        )}
                        {question.isPublished ? (
                          <ApproveIcon
                            title="Approved"
                            className="mr-3 h-5 w-5 text-green-400"
                          />
                        ) : (
                          <Disapprove />
                        )}
                        <UnlinkIcon
                          onClick={() =>
                            confirmQuestionUnlink(question.questionId)
                          }
                          className="w-4 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div
          className={`flex justify-between items-center ${
            questions.length === 0 ? "hidden" : ""
          }`}
        >
          <Pagination
            pages={totalPages}
            setPageNumber={setPageNumber}
            ref={paginationRef}
          />
          <span>
            <span className="text-sm pr-4 ml-auto text-left-700">
              Showing {pageNumber} to{" "}
              {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
              {totalRecords} results
            </span>

            <SelectPageSize
              setPageSize={setPageSize}
              type={"second"}
              ref={pageSizeRef}
            />
          </span>
        </div>

        {questions.length === 0 && <p className="p-2">No Records Found</p>}
      </div>
      <div>
        {isDelink &&
          showLinkedStandards &&
          linkedStandards &&
          linkedStandards.length > 0 && (
            <DelinkStandardPopup
              standardId={standardUnlink}
              title={"Linked Standards"}
              buttonText={"Ok"}
              confirmAction={handleDelinkConfirmation}
              linkedStandardsData={linkedStandards}
            />
          )}
      </div>
    </Fragment>
  );
}

export default AssociatedQuestion;
